import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, fetchFileUrl, uploadFile } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { Button, EmptyState, ErrorText, Modal, Spinner, Tag, Textarea, cls, fmtDateTime } from '../../lib/ui.tsx';

interface ThreadSummary {
  student: { id: string; name: string };
  lastMessage: { id: string; body: string; createdAt: string } | null;
  unread: number;
}

interface Message {
  id: string;
  senderId: string;
  body: string;
  attachmentFileId: string | null;
  exerciseId: string | null;
  takeId: string | null;
  createdAt: string;
  sender: { id: string; name: string; role: string };
}

/** F4 · Attachment renderer — image preview if it decodes, download link otherwise. */
function Attachment({ fileId }: { fileId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(true);
  useEffect(() => {
    let alive = true;
    void fetchFileUrl(fileId)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
    };
  }, [fileId]);
  if (!url) return null;
  return isImage ? (
    <img src={url} alt="Attachment" onError={() => setIsImage(false)} className="mt-2 max-h-48 rounded-lg" />
  ) : (
    <a href={url} download className="mt-2 block text-sm underline">
      📎 Attachment
    </a>
  );
}

function Bubble({ msg, own }: { msg: Message; own: boolean }) {
  return (
    <div className={cls('flex', own ? 'justify-end' : 'justify-start')}>
      <div
        className={cls(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm',
          own ? 'bg-brand-600 text-white' : 'border border-slate-200 bg-white text-slate-800',
        )}
      >
        <p className="whitespace-pre-wrap break-words">{msg.body}</p>
        {msg.attachmentFileId && <Attachment fileId={msg.attachmentFileId} />}
        {msg.takeId && (
          <Link
            to={`/coach/review/${msg.takeId}`}
            className={cls('mt-1 block text-xs underline', own ? 'text-brand-100' : 'text-brand-700')}
          >
            🎙️ take
          </Link>
        )}
        <p className={cls('mt-1 text-[10px]', own ? 'text-brand-200' : 'text-slate-400')}>{fmtDateTime(msg.createdAt)}</p>
      </div>
    </div>
  );
}

/** F1 · Coach↔student thread with composer + F4 attachments. */
function ThreadView({ studentId, studentName }: { studentId: string; studentName?: string }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const scrollBox = useRef<HTMLDivElement>(null);

  const { data: messages, isLoading } = useQuery({
    queryKey: ['msg-thread', studentId],
    queryFn: () => api<Message[]>(`/api/messages/thread/${studentId}`),
    refetchInterval: 10_000,
  });

  useEffect(() => {
    const el = scrollBox.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages?.length]);

  const send = useMutation({
    mutationFn: () =>
      api<Message>('/api/messages', {
        body: { studentId, body, attachmentFileId: attachment?.id ?? null },
      }),
    onSuccess: () => {
      setBody('');
      setAttachment(null);
      void qc.invalidateQueries({ queryKey: ['msg-thread', studentId] });
      void qc.invalidateQueries({ queryKey: ['msg-threads'] });
    },
  });

  async function onPickFile(file: File) {
    setUploading(true);
    setUploadError(null);
    try {
      const up = await uploadFile(file, file.name);
      setAttachment({ id: up.id, name: file.name });
    } catch (err) {
      setUploadError(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex h-[70vh] flex-col rounded-xl border border-slate-200 bg-slate-50">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <button className="text-sm text-brand-700 md:hidden" onClick={() => navigate('/coach/messages')}>
          ← Back
        </button>
        <p className="text-sm font-semibold">{studentName ?? 'Conversation'}</p>
      </header>
      <div ref={scrollBox} className="flex-1 space-y-2 overflow-y-auto p-3">
        {isLoading ? (
          <Spinner />
        ) : !messages || messages.length === 0 ? (
          <p className="p-6 text-center text-xs text-slate-400">No messages yet — say hi 👋</p>
        ) : (
          messages.map((m) => <Bubble key={m.id} msg={m} own={m.senderId === me?.id} />)
        )}
      </div>
      <div className="border-t border-slate-200 bg-white p-3">
        {attachment && (
          <p className="mb-2 flex items-center gap-2 text-xs text-slate-600">
            📎 {attachment.name}
            <button className="text-red-600 underline" onClick={() => setAttachment(null)}>
              remove
            </button>
          </p>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPickFile(f);
              e.target.value = '';
            }}
          />
          <Button variant="secondary" onClick={() => fileInput.current?.click()} disabled={uploading} aria-label="Attach file">
            {uploading ? '…' : '📎'}
          </Button>
          <Textarea
            rows={2}
            placeholder="Write a message…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button onClick={() => send.mutate()} disabled={send.isPending || body.trim().length === 0}>
            Send
          </Button>
        </div>
        <ErrorText error={send.error ?? uploadError} />
      </div>
    </div>
  );
}

/** F2 · Broadcast to every active student. */
function BroadcastModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [sentTo, setSentTo] = useState<number | null>(null);
  const send = useMutation({
    mutationFn: () => api<{ recipients: number }>('/api/messages/broadcast', { body: { body } }),
    onSuccess: (res) => {
      setSentTo(res.recipients);
      setBody('');
      void qc.invalidateQueries({ queryKey: ['msg-threads'] });
    },
  });
  return (
    <Modal
      open={open}
      onClose={() => {
        setSentTo(null);
        onClose();
      }}
      title="Broadcast to all students"
    >
      {sentTo !== null ? (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Sent to {sentTo} students ✅</p>
      ) : (
        <div className="space-y-3">
          <Textarea rows={4} placeholder="Announcement…" value={body} onChange={(e) => setBody(e.target.value)} />
          <Button onClick={() => send.mutate()} disabled={send.isPending || body.trim().length === 0}>
            Send to everyone
          </Button>
          <ErrorText error={send.error} />
        </div>
      )}
    </Modal>
  );
}

export default function MessagesPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const { data: threads, isLoading } = useQuery({
    queryKey: ['msg-threads'],
    queryFn: () => api<ThreadSummary[]>('/api/messages/threads'),
    refetchInterval: 30_000,
  });

  const activeName = threads?.find((t) => t.student.id === studentId)?.student.name;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Messages</h1>
        <Button variant="secondary" onClick={() => setBroadcastOpen(true)}>
          📣 Broadcast
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <div className={cls('space-y-1', studentId && 'hidden md:block')}>
          {isLoading ? (
            <Spinner />
          ) : !threads || threads.length === 0 ? (
            <EmptyState title="No students yet" hint="Threads appear once you have active students." />
          ) : (
            threads.map((t) => (
              <button
                key={t.student.id}
                onClick={() => navigate(`/coach/messages/${t.student.id}`)}
                className={cls(
                  'flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left',
                  t.student.id === studentId
                    ? 'border-brand-300 bg-brand-50'
                    : 'border-slate-200 bg-white hover:border-brand-200',
                )}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t.student.name}</p>
                  <p className="truncate text-xs text-slate-500">{t.lastMessage?.body ?? 'No messages yet'}</p>
                </div>
                {t.unread > 0 && <Tag tone="brand">{t.unread}</Tag>}
              </button>
            ))
          )}
        </div>

        <div className={cls(!studentId && 'hidden md:block')}>
          {studentId ? (
            <ThreadView studentId={studentId} studentName={activeName} />
          ) : (
            <EmptyState title="Pick a conversation" hint="Select a student on the left to open the thread." />
          )}
        </div>
      </div>

      <BroadcastModal open={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
    </div>
  );
}
